import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import { APIContext } from '../../api/APIProvider';
import { Button, Icon } from '@monash/portal-react';
import IdleTimer from './IdleTimer';
import c from './impersonation-wrapper.module.scss';
import portalLink from '../../utils/portalLinks';
import { MOCK_MONASH_OBJECT_ID } from '../../constants/monash-object-id';
import useSessionStorage from '../../hooks/use-session-storage';
import { MockContext } from '../mock-provider/MockProvider';

export const ImpersonationContext = React.createContext();

const ImpersonationWrapper = ({
  time = 60 * 30, // session length in seconds, default 30 mins
  children,
}) => {
  const { user, logout } = useContext(AuthContext);
  const canImpersonate = user?.roles?.includes(
    'studentportal-support-impersonate',
    // TODO add impersonate mock user role
  );

  const mockContextValues = useContext(MockContext);

  const { setMockUserId, updateMockApiConfig, mockApisAreActive } =
    mockContextValues ?? {};

  const { isImpersonating, endImpersonation } = useContext(APIContext);

  const [impersonating, setImpersonating] = useState(false);
  const [checkingImpersonationState, setCheckingImpersonationState] =
    useState(canImpersonate);
  const [impersonateeData, setImpersonateeData] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  const [useMocks] = useSessionStorage('fakeData');

  useEffect(() => {
    // TODO; Differentiate perms between impersonating mock and real users
    if (canImpersonate) {
      isImpersonating().then((r) => {
        const data = {
          ...r,
          successfulIdentityResponse: true,
        };

        if (r.isMockUser) {
          if (!mockContextValues) {
            return setCheckingImpersonationState(false);
          }

          setMockUserId(data.uid);
          updateMockApiConfig({
            baseValue: true,
            configUpdate: {
              isImpersonating: false,
              endImpersonation: false,
            },
          });

          // if mock APIs aren't active yet, we need to wait for mocks to load before rendering
          // the app, otherwise real API calls get made while mocks are loading
          if (!mockApisAreActive) return;
        }

        setImpersonating(!!r?.currentlyImpersonating);
        setImpersonateeData(data);
        setCheckingImpersonationState(false);
      });
    }
  }, [canImpersonate, isImpersonating, mockApisAreActive]);

  useEffect(() => {
    const timer = new IdleTimer({
      timeout: time,
      onTimeout: async () => {
        if (canImpersonate && !!impersonating) {
          const module = await import('html2canvas');
          const html2canvas = module.default;

          html2canvas(document.body).then((canvas) => {
            const base64image = canvas.toDataURL('image/png');
            setScreenshot(base64image);
          });
          setTimedOut(true);
          logout();
          return true;
        } else {
          return false;
        }
      },
      // onExpired: () => {}, //already expired on load
      onRefresh: () => {},
    });

    return () => {
      timer.cleanUp();
    };
  }, [canImpersonate, impersonating, logout, time]);

  const blurry = (
    <div style={{ height: '100%' }}>
      <div
        className={c.screenshot}
        style={{ backgroundImage: `url(${screenshot})` }}
      >
        {' '}
      </div>
      <div>
        <div className={c.modal}>
          <div className={c.box}>
            <h1>Session Timeout</h1>
            <p>
              Your impersonation session has expired. Please return to the
              Portal Admin application to continue.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const childrenWithBanner = (
    <div className={c.container}>
      <div className={c.banner}>
        <div className={c.bannerContent}>
          <span className={c.bannerText}>
            <Icon.Warning /> You're temporarily logged in as{' '}
            {impersonateeData?.preferredName} {impersonateeData?.lastName}
          </span>
          <span className={c.bannerRight}>
            <div className={c.endButton}>
              <Button
                variant="delete"
                size="medium"
                onClick={() => {
                  endImpersonation()
                    // .then(() => window.close())
                    .then(() => {
                      window.open(portalLink('admin'), '_self');
                    });
                }}
              >
                End
              </Button>
            </div>
          </span>
        </div>
      </div>
      <div className={c.content}>{children}</div>
    </div>
  );

  // the user the app currently cares about (either you or the person you are impersonating)
  const currentUser = useMemo(() => {
    const finalUser = impersonating ? impersonateeData : user;
    return { ...finalUser, ...(useMocks && { uid: MOCK_MONASH_OBJECT_ID }) };
  }, [impersonating, impersonateeData, user]);

  return (
    <ImpersonationContext.Provider value={{ currentUser }}>
      {/* Most users, cant impersonate, just show children */}
      {!canImpersonate && children}

      {/* can impersonate, begin... */}
      {canImpersonate && (
        <>
          {/* currenly waiting for result of isImpersonating(), loading */}
          {checkingImpersonationState && <div>...</div>}

          {/* not impersonating, behave normally */}
          {!checkingImpersonationState && !impersonating && children}
          {/* currently impersonating, need to check timed out */}
          {!checkingImpersonationState && impersonating && (
            <>
              {/* timed out, show blurred screen */}
              {timedOut && blurry}
              {/* still impersonating, show children with banner */}
              {!timedOut && childrenWithBanner}
            </>
          )}
        </>
      )}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationWrapper;
